import React, { useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import 'moment/locale/en-gb';
import {
  Button, message, Table, Alert,
  Typography, Row, Switch,
} from 'antd';
import { connect } from 'react-redux';
import ConfigHeading from '../../configure/components/ConfigHeading';
import { selectPackages } from '../../../../state/domain/packages/packageReducer';
import EditPackage from './EditPackage';
import {
  editPackage, requestAllPackages, requestToggleActive, requestTerminatePackage,
} from '../../../../state/ui/configuration/packages/packageUiActions';
import { selectToggleStatus, selectFetchPackagesRequest } from '../../../../state/ui/configuration/packages/packageUiReducer';
import ConfirmButton from '../../../../components/buttons/ConfirmButton';
import { getDateOfExpiry, DATE_FORMAT } from '../packageUtil';

const { Column } = Table;
const { Text } = Typography;

// TABLE
function PackagesTable({
  isLoading, packages, onEditButton, toggleActive, toggleStatus, terminatePackage,
}) {
  const [visiblePackages, setVisiblePackages] = useState([]);

  const intl = useIntl();
  const RowTotal = () => (
    <div>
      <Text>{`${visiblePackages.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );
  const isActiveLoading = record => toggleStatus.find(code => code === record.code);

  useEffect(() => {
    const tempPackages = packages.slice();
    setVisiblePackages(tempPackages
      .filter(p => !p.registration && !p.adhoc) // Remove irrelevant packages.
      .map(p => ({ // Format nicely for the table
        ...p,
        packageStartDate: moment.utc(p.packageStartDate).format(DATE_FORMAT),
        packageEndDate: p.packageEndDate ? moment.utc(p.packageEndDate).format(DATE_FORMAT) : 'N/A',
        price: p.price.toFixed(2),
      })));
  }, [packages]);

  const onActiveUpdate = (record) => {
    const realPackage = packages.find(realPack => realPack.code === record.code);
    // Only toggle if the package from date is in the future
    if (moment.utc().isBefore(moment.utc(realPackage.packageStartDate))) toggleActive(realPackage);
    else message.warn(intl.formatMessage({ id: realPackage.active ? 'packageUnPublishInvalid' : 'packagePublishInvalid' }));
  };


  const onKillButton = (record) => {
    const realPackage = packages.find(realPack => realPack.code === record.code);
    terminatePackage(realPackage);
  };

  function sortByTitle(a, b) {
    const aStr = `${a.title}`;
    const bStr = `${b.title}`;
    return aStr.localeCompare(bStr);
  }

  function sortByFromDate(a, b) {
    const adate = moment.utc(a.packageStartDate, DATE_FORMAT);
    const bdate = moment.utc(b.packageStartDate, DATE_FORMAT);
    if (adate.isBefore(bdate)) {
      return 1;
    }
    if (adate.isAfter(bdate)) {
      return -1;
    }
    return 0;
  }
  function sortByToDate(a, b) {
    if (a.packageEndDate === b.packageEndDate) { return sortByFromDate(a, b); }
    if (b.packageEndDate === 'N/A') {
      return 1;
    }
    if (a.packageEndDate === 'N/A') {
      return -1;
    }
    const adate = moment.utc(a.packageEndDate, DATE_FORMAT);
    const bdate = moment.utc(b.packageEndDate, DATE_FORMAT);
    if (adate.isBefore(bdate)) {
      return 1;
    }
    if (adate.isAfter(bdate)) {
      return -1;
    }
    return sortByFromDate(a, b);
  }

  function sortByPublished(a, b) {
    if (a.active && !b.active) return 1;
    if (!a.active && b.active) return -1;
    return -sortByToDate(a, b);
  }

  return (
    <Table
      pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
      scroll={{ x: 1, y: false }}
      loading={isLoading}
      dataSource={visiblePackages}
      rowKey={record => record.code}
    >
      <Column
        title={<FormattedMessage id="published" />}
        dataIndex="active"
        key="active"
        sorter={sortByPublished}
        width={120}
        render={(active, record) => (
          <Row type="flex" justify="center">
            <Switch loading={!!isActiveLoading(record)} onChange={() => onActiveUpdate(record)} size="small" checked={active} />
          </Row>
        )}
      />
      <Column
        sorter={sortByTitle}
        title={<FormattedMessage id="packageName" />}
        dataIndex="title"
        key="title"
      />
      <Column
        sorter={sortByFromDate}
        title={<FormattedMessage id="startDate" />}
        dataIndex="packageStartDate"
        key="packageStartDate"
      />
      <Column
        sorter={sortByToDate}
        defaultSortOrder="ascend"
        title={<FormattedMessage id="endDate" />}
        dataIndex="packageEndDate"
        key="packageEndDate"
      />
      <Column
        title={<FormattedMessage id="price" />}
        dataIndex="price"
        key="price"
      />
      <Column
        title={<FormattedMessage id="currency" />}
        dataIndex="currencyCode"
        key="currencyCode"
      />
      <Column
        title={<FormattedMessage id="credits" />}
        dataIndex="mainTokens"
        key="mainTokens"
      />
      <Column
        title={<FormattedMessage id="bonusCredits" />}
        dataIndex="extraTokens"
        key="extraTokens"
      />
      <Column
        className="via-action-column"
        align="right"
        width="70px"
        render={(text, record) => {
          const startDate = moment.utc(record.packageStartDate, DATE_FORMAT);
          const endDate = record.packageEndDate !== 'N/A' ? moment.utc(record.packageEndDate, DATE_FORMAT) : null;
          const editable = !record.active || !endDate || moment.utc().isBefore(endDate);
          const killable = record.active && moment.utc().isAfter(startDate) && (!endDate || moment.utc().isBefore(endDate));
          return (
            <div className="button-flex-col">
              {killable && (
                <ConfirmButton size="small" type="danger" onClick={() => onKillButton(record)} icon="stop">
                  <FormattedMessage id="terminate" />
                </ConfirmButton>
              )}
              <Button size="small" type="primary" onClick={() => onEditButton(record)} icon="edit">
                <FormattedMessage id={editable ? 'edit' : 'view'} />
              </Button>
            </div>
          );
        }}
      />
    </Table>
  );
}

PackagesTable.propTypes = {
  isLoading: PropTypes.bool,
  onEditButton: PropTypes.func.isRequired,
  packages: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      packageStartDate: PropTypes.string.isRequired,
      packageEndDate: PropTypes.string,
      currencyCode: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
      mainTokens: PropTypes.number.isRequired,
      extraTokens: PropTypes.number,
      active: PropTypes.bool.isRequired,
    }),
  ),
  toggleActive: PropTypes.func.isRequired,
  toggleStatus: PropTypes.arrayOf(PropTypes.number),
  terminatePackage: PropTypes.func.isRequired,
};

PackagesTable.defaultProps = {
  isLoading: false,
  packages: [],
  toggleStatus: [],
};

function CreditPackages({
  isLoading, loadPageData, packages,
  onCreateOrEdit, toggleActive, toggleStatus, terminatePackage,
}) {
  // Load users on first entry - these only need to be loaded once.
  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  const intl = useIntl();

  const CreateButton = () => (
    <Button type="primary" onClick={() => onCreateOrEdit(null)} loading={isLoading} icon="plus">
      <FormattedMessage id="create" />
    </Button>
  );

  return (
    <ConfigHeading
      titleId="creditPackages"
      actions={<CreateButton />}
    >
      {!isLoading ? ['EUR', 'USD', 'GBP'].map((currencyCode) => {
        const packagesForCurrency = packages.filter(p => p.currencyCode === currencyCode && p.active && !p.registration && !p.adhoc);
        const momentOfExpiry = getDateOfExpiry(packagesForCurrency, moment.utc());
        const dateOfExpiry = momentOfExpiry ? momentOfExpiry.format(DATE_FORMAT) : null;
        return (
          dateOfExpiry ? (
            <Alert
              key={currencyCode}
              description={intl.formatMessage({ id: 'packageExpiring' }, { currencyCode, dateOfExpiry })}
              type="info"
              showIcon
            />
          ) : null
        );
      }) : null}
      <PackagesTable
        isLoading={isLoading}
        packages={packages}
        onEditButton={onCreateOrEdit}
        toggleActive={toggleActive}
        toggleStatus={toggleStatus}
        terminatePackage={terminatePackage}
        intl={intl}
      />
      <EditPackage />
    </ConfigHeading>
  );
}

// Validate the prop type parameters.
CreditPackages.propTypes = {
  isLoading: PropTypes.bool,
  loadPageData: PropTypes.func.isRequired,
  packages: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    packageStartDate: PropTypes.string.isRequired,
    packageEndDate: PropTypes.string,
    currencyCode: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    mainTokens: PropTypes.number.isRequired,
    extraTokens: PropTypes.number,
    active: PropTypes.bool.isRequired,
    registration: PropTypes.bool,
  })),
  onCreateOrEdit: PropTypes.func.isRequired,
  toggleActive: PropTypes.func.isRequired,
  toggleStatus: PropTypes.arrayOf(PropTypes.number),
  terminatePackage: PropTypes.func.isRequired,
};

CreditPackages.defaultProps = {
  isLoading: false,
  packages: [],
  toggleStatus: [],
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectFetchPackagesRequest(state);

  return {
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    error: pageDataRequest ? pageDataRequest.error : null,
    packages: selectPackages(state),
    toggleStatus: selectToggleStatus(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: () => dispatch(requestAllPackages()),
  onCreateOrEdit: pack => dispatch(editPackage(pack ? pack.code : null)),
  toggleActive: pack => dispatch(requestToggleActive(pack)),
  terminatePackage: pack => dispatch(requestTerminatePackage(pack)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(CreditPackages);
